/**
 * metaLeadGen/dailyMetrics.ts
 *
 * Daily aggregation layer for the Meta Lead Generation module.
 *
 * Rules:
 * - Filters are applied first via the existing filter layer.
 * - Raw totals are summed per day before any ratio is calculated.
 * - CPM, CTR, CPL, Bounce Rate, and Conversion Rate are NEVER averaged from rows.
 *   They are always derived from the aggregated daily totals.
 * - All values remain unrounded internally. Rounding only happens in formatters.ts.
 * - Missing dates inside the selected range are filled with zeros.
 * - YYYY-MM-DD is used everywhere — no timestamps, no display dates in calculations.
 *
 * Exports:
 *   getDailyMetrics(rows, filters)             → DailyMetricsResult
 *   getPreviousPeriodDailyMetrics(rows, filters) → DailyMetricsResult
 *   validateDailyVsSummary(dailyRows, summary) → DailyValidationResult
 *   CHART_METRIC_CONFIG                        → chart axis/format config
 */

import type {
  MetaLeadGenRow,
  MetaLeadGenFilters,
  MetaLeadGenMetrics,
} from "./types";
import { applyFilters, getPreviousPeriodRows } from "./filters";
import { aggregateTotals, safeDivide } from "./calculations";
import {
  formatCurrency,
  formatCpm,
  formatPercent,
  formatNumber,
  formatDecimal,
} from "./formatters";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

/** One day's fully-computed metrics row. */
export interface DailyMetricRow {
  // Identity
  date: string;              // YYYY-MM-DD

  // Raw summed totals — never derived, always summed from rows
  spend: number;
  impressions: number;
  clicks: number;
  landingPageViews: number;
  leads: number;
  bounces: number;

  // Derived metrics — calculated from this day's totals only
  // (never averaged from sub-rows — safe division throughout)
  cpm: number;               // spend / impressions * 1000
  ctr: number;               // clicks / impressions * 100
  cpl: number;               // spend / leads
  bounceRate: number;        // bounces / landingPageViews * 100
  conversionRate: number;    // leads / landingPageViews * 100
  frequency: number;         // impressions / reach

  // Tooltip-ready display strings — produced by formatters.ts only
  display: {
    date: string;
    spend: string;
    impressions: string;
    clicks: string;
    landingPageViews: string;
    leads: string;
    cpm: string;
    ctr: string;
    cpl: string;
    bounceRate: string;
    conversionRate: string;
    frequency: string;
  };
}

/** Aggregate helper values for chart rendering (axis domains, reference lines). */
export interface ChartMeta {
  // Overall averages — derived from full-period totals, not averaged per day
  averageCpm: number;
  averageCtr: number;
  averageBounceRate: number;
  averageConversionRate: number;

  // Daily averages — totalMetric / numDays (for reference lines on trend charts)
  // averageCpl is period-level (totalSpend / totalLeads), never an average of daily CPLs
  averageSpend: number;
  averageLeads: number;
  averageCpl: number;
  averageFrequency: number;

  // Max daily values — useful for Y-axis domain calculation
  maxSpend: number;
  maxClicks: number;
  maxLeads: number;
  maxImpressions: number;
  maxLandingPageViews: number;
}

/** Full result returned by getDailyMetrics. */
export interface DailyMetricsResult {
  dailyRows: DailyMetricRow[];
  chartMeta: ChartMeta;
}

/** Result of the debug validation check. */
export interface DailyValidationResult {
  passed: boolean;
  checks: Array<{
    metric: string;
    dailySum: number;
    summaryValue: number;
    delta: number;
    passed: boolean;
  }>;
}

// ─────────────────────────────────────────────
// CHART METRIC CONFIG
// Maps each metric field to its display format type.
// Consumed by the chart layer to determine axis labels and tooltips.
// ─────────────────────────────────────────────
export type ChartMetricFormat = "currency" | "percent" | "number";

export const CHART_METRIC_CONFIG: Record<
  Exclude<keyof DailyMetricRow, "date" | "display">,
  ChartMetricFormat
> = {
  spend:            "currency",
  cpm:              "currency",
  cpl:              "currency",
  ctr:              "percent",
  bounceRate:       "percent",
  conversionRate:   "percent",
  impressions:      "number",
  clicks:           "number",
  landingPageViews: "number",
  leads:            "number",
  bounces:          "number",
  frequency:        "number",
};

// ─────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────

/**
 * Generates all YYYY-MM-DD strings between start and end (inclusive).
 * Uses UTC to avoid DST-related day-skip bugs.
 */
function generateDateRange(start: string, end: string): string[] {
  const dates: string[] = [];
  let current = start;
  // Guard: if start > end, return empty (should not happen with valid inputs)
  while (current <= end) {
    dates.push(current);
    const d = new Date(current + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + 1);
    current = d.toISOString().slice(0, 10);
  }
  return dates;
}

/**
 * Calculates the previous period date range for a given date range.
 * Mirrors the logic in filters.ts getPreviousPeriodRows.
 * e.g. Jan 13–23 (11 days) → previous = Jan 2–12 (11 days)
 */
function getPreviousDateRange(
  dateRange: { start: string; end: string }
): { start: string; end: string } {
  const startMs = new Date(dateRange.start + "T00:00:00Z").getTime();
  const endMs   = new Date(dateRange.end   + "T00:00:00Z").getTime();
  const durationMs = endMs - startMs + 86400000; // +1 day for inclusive end

  const prevEndMs   = startMs - 86400000;
  const prevStartMs = prevEndMs - durationMs + 86400000;

  return {
    start: new Date(prevStartMs).toISOString().slice(0, 10),
    end:   new Date(prevEndMs).toISOString().slice(0, 10),
  };
}

/**
 * Formats a YYYY-MM-DD date for tooltip display.
 * e.g. "2026-01-13" → "Jan 13"
 */
function formatDisplayDate(date: string): string {
  const d = new Date(date + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * Builds one DailyMetricRow from a date, its aggregated totals, and day-level reach.
 * All derived metrics computed here — never elsewhere.
 */
function buildDailyRow(
  date: string,
  totals: ReturnType<typeof aggregateTotals>,
  dayReach: number
): DailyMetricRow {
  const cpm            = safeDivide(totals.spend, totals.impressions) * 1000;
  const ctr            = safeDivide(totals.clicks, totals.impressions) * 100;
  const cpl            = safeDivide(totals.spend, totals.leads);
  const bounceRate     = safeDivide(totals.bounces, totals.landingPageViews) * 100;
  const conversionRate = safeDivide(totals.leads, totals.landingPageViews) * 100;
  const frequency      = safeDivide(totals.impressions, dayReach);

  return {
    date,
    spend:            totals.spend,
    impressions:      totals.impressions,
    clicks:           totals.clicks,
    landingPageViews: totals.landingPageViews,
    leads:            totals.leads,
    bounces:          totals.bounces,
    cpm,
    ctr,
    cpl,
    bounceRate,
    conversionRate,
    frequency,
    display: {
      date:             formatDisplayDate(date),
      spend:            formatCurrency(totals.spend),
      impressions:      formatNumber(totals.impressions),
      clicks:           formatNumber(totals.clicks),
      landingPageViews: formatNumber(totals.landingPageViews),
      leads:            formatNumber(totals.leads),
      cpm:              formatCpm(cpm),
      ctr:              formatPercent(ctr),
      cpl:              formatCpm(cpl),
      bounceRate:       formatPercent(bounceRate),
      conversionRate:   formatPercent(conversionRate),
      frequency:        `${formatDecimal(frequency, 2)}×`,
    },
  };
}

/**
 * Core computation: group already-filtered rows by date, fill missing
 * dates, compute daily metrics, build chart meta.
 */
function computeDailyFromRows(
  rows: MetaLeadGenRow[],
  dateRange: { start: string; end: string }
): DailyMetricsResult {
  // ── Step 1: Group rows by date ──────────────────────────────────────────
  const byDate = new Map<string, MetaLeadGenRow[]>();
  for (const row of rows) {
    const bucket = byDate.get(row.date);
    if (bucket) {
      bucket.push(row);
    } else {
      byDate.set(row.date, [row]);
    }
  }

  // ── Step 2: Generate all dates in range (fills gaps with zero rows) ─────
  const allDates = generateDateRange(dateRange.start, dateRange.end);

  // ── Step 3: Aggregate + derive metrics per day, sorted ascending ────────
  const dailyRows: DailyMetricRow[] = allDates.map(date => {
    const dayRows = byDate.get(date) ?? [];
    const totals  = aggregateTotals(dayRows);
    const dayReach = dayRows.reduce((sum, r) => sum + (r.reach ?? 0), 0);
    return buildDailyRow(date, totals, dayReach);
  });

  // ── Step 4: Chart meta ───────────────────────────────────────────────────
  // Overall averages come from full-period aggregated totals (same as exec summary).
  // This is correct — they must NOT be averages of daily ratios.
  const fullTotals = aggregateTotals(rows);
  const totalReach = rows.reduce((sum, r) => sum + (r.reach ?? 0), 0);
  const numDays = dailyRows.length || 1;
  const chartMeta: ChartMeta = {
    averageCpm:            safeDivide(fullTotals.spend, fullTotals.impressions) * 1000,
    averageCtr:            safeDivide(fullTotals.clicks, fullTotals.impressions) * 100,
    averageBounceRate:     safeDivide(fullTotals.bounces, fullTotals.landingPageViews) * 100,
    averageConversionRate: safeDivide(fullTotals.leads, fullTotals.landingPageViews) * 100,
    averageSpend:          fullTotals.spend / numDays,
    averageLeads:          fullTotals.leads / numDays,
    averageCpl:            safeDivide(fullTotals.spend, fullTotals.leads),
    averageFrequency:      safeDivide(fullTotals.impressions, totalReach),
    maxSpend:            Math.max(0, ...dailyRows.map(r => r.spend)),
    maxClicks:           Math.max(0, ...dailyRows.map(r => r.clicks)),
    maxLeads:            Math.max(0, ...dailyRows.map(r => r.leads)),
    maxImpressions:      Math.max(0, ...dailyRows.map(r => r.impressions)),
    maxLandingPageViews: Math.max(0, ...dailyRows.map(r => r.landingPageViews)),
  };

  return { dailyRows, chartMeta };
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

/**
 * Main function: applies filters, groups by date, fills missing dates,
 * derives metrics per day, and returns chart-ready output.
 *
 * @param rows    - Full raw row array (e.g. RAW_META_LEAD_GEN_ROWS)
 * @param filters - Active filters including a required dateRange
 * @returns DailyMetricsResult with dailyRows (sorted ASC) and chartMeta
 */
export function getDailyMetrics(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters & { dateRange: { start: string; end: string } }
): DailyMetricsResult {
  const filtered = applyFilters(rows, filters);
  return computeDailyFromRows(filtered, filters.dateRange);
}

/**
 * Previous-period variant: shifts the date range back by the same duration,
 * applies all non-date filters, and returns daily metrics for the prior period.
 *
 * Useful for:
 *   - Overlaying previous-period lines on trend charts
 *   - Day-over-day comparison tooltips
 *
 * @param rows    - Full raw row array
 * @param filters - Active filters with current dateRange
 * @returns DailyMetricsResult for the equivalent prior period
 */
export function getPreviousPeriodDailyMetrics(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters & { dateRange: { start: string; end: string } }
): DailyMetricsResult {
  // Get rows scoped to previous period dates
  const prevDateRows = getPreviousPeriodRows(rows, filters.dateRange);

  // Apply all non-date filters to previous period rows
  const { dateRange: _ignored, ...nonDateFilters } = filters;
  const prevRows = applyFilters(prevDateRows, nonDateFilters);

  // Compute the matching date range for the previous period
  const prevDateRange = getPreviousDateRange(filters.dateRange);

  return computeDailyFromRows(prevRows, prevDateRange);
}

/**
 * Debug/validation utility: verifies that the sum of daily raw totals matches
 * the executive summary metrics exactly (within floating-point tolerance).
 *
 * Checks: spend, impressions, clicks, landingPageViews, leads
 * (Ratio metrics like CPM/CTR are not summed — they are re-derived.)
 *
 * @param dailyRows - Output from getDailyMetrics
 * @param summary   - Executive summary metrics from getMetaLeadGenComparison
 */
export function validateDailyVsSummary(
  dailyRows: DailyMetricRow[],
  summary: MetaLeadGenMetrics
): DailyValidationResult {
  const TOLERANCE = 0.001; // floating-point safe threshold

  const metricsToCheck: Array<keyof MetaLeadGenMetrics & keyof DailyMetricRow> = [
    "spend",
    "impressions",
    "clicks",
    "landingPageViews",
    "leads",
  ];

  const checks = metricsToCheck.map(metric => {
    const dailySum     = dailyRows.reduce((sum, row) => sum + (row[metric] as number), 0);
    const summaryValue = summary[metric] as number;
    const delta        = Math.abs(dailySum - summaryValue);
    return {
      metric,
      dailySum,
      summaryValue,
      delta,
      passed: delta < TOLERANCE,
    };
  });

  return {
    passed: checks.every(c => c.passed),
    checks,
  };
}
